import { z } from 'zod';
import { isoDate, spaceKind, uuid } from './common';

export const spaceRole = z.enum(['OWNER', 'MEMBER']);
export type SpaceRole = z.infer<typeof spaceRole>;

/** A space as the selector and the Spaces page see it: what it is, and what you are in it. */
export const spaceSummary = z.object({
  id: uuid,
  kind: spaceKind,
  name: z.string(),
  icon: z.string().nullable(),
  /** OWNER of your private space; MEMBER of the team space; your row in a custom one; null when you merely see it. */
  myRole: spaceRole.nullable(),
  canManage: z.boolean(),
  memberCount: z.number().int(),
  meetingCount: z.number().int(),
  folderCount: z.number().int(),
});
export type SpaceSummary = z.infer<typeof spaceSummary>;

export const spaceMember = z.object({
  userId: uuid,
  email: z.string(),
  name: z.string().nullable(),
  role: spaceRole,
  since: isoDate,
});
export type SpaceMember = z.infer<typeof spaceMember>;

/**
 * What fills a folder by itself (2026-09-19). Every condition that is set must
 * match. Nothing set is no rule at all — a folder you fill by hand, which is
 * the default. The rule is a live view, never a copy.
 */
export const folderRule = z.object({
  /** Everyone listed was in the meeting. */
  people: z.array(z.string().email()).max(20).default([]),
  /** Anybody from one of these companies was in it, by address domain. */
  domains: z.array(z.string().trim().min(2).max(120)).max(10).default([]),
  /** It is an instance of one of these recurring meetings. */
  seriesKeys: z.array(z.string().min(1).max(200)).max(20).default([]),
  /** Its title contains this, however it is capitalised. */
  titleContains: z.string().trim().min(1).max(120).nullable().default(null),
});
export type FolderRule = z.infer<typeof folderRule>;

export const folderSummary = z.object({
  id: uuid,
  name: z.string(),
  icon: z.string().nullable(),
  description: z.string().nullable(),
  /** Null for a folder of your own that lives in no space. */
  spaceId: uuid.nullable(),
  favorite: z.boolean(),
  position: z.number().int(),
  meetingCount: z.number().int(),
  canManage: z.boolean(),
  /** Null for a folder filled by hand. */
  rule: folderRule.nullable(),
  /** The rule in words, for the folder's header. Null when there is none. */
  ruleWords: z.string().nullable(),
});
export type FolderSummary = z.infer<typeof folderSummary>;

export const spaceDetail = spaceSummary.extend({
  members: z.array(spaceMember),
  folders: z.array(folderSummary),
});
export type SpaceDetail = z.infer<typeof spaceDetail>;

export const createSpaceBody = z.object({
  name: z.string().trim().min(1).max(80),
  icon: z.string().trim().max(16).nullable().optional(),
});
export type CreateSpaceBody = z.infer<typeof createSpaceBody>;

export const updateSpaceBody = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  icon: z.string().trim().max(16).nullable().optional(),
});
export type UpdateSpaceBody = z.infer<typeof updateSpaceBody>;

export const addSpaceMemberBody = z.object({
  email: z.string().email().max(320),
  role: spaceRole.default('MEMBER'),
});
export type AddSpaceMemberBody = z.infer<typeof addSpaceMemberBody>;

export const createFolderBody = z.object({
  name: z.string().trim().min(1).max(80),
  icon: z.string().trim().max(16).nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  spaceId: uuid.nullable().optional(),
  rule: folderRule.nullable().optional(),
});
export type CreateFolderBody = z.infer<typeof createFolderBody>;

export const updateFolderBody = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  icon: z.string().trim().max(16).nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  favorite: z.boolean().optional(),
  position: z.number().int().min(0).optional(),
  /** Null turns the rule off and keeps whatever is already inside. */
  rule: folderRule.nullable().optional(),
});
export type UpdateFolderBody = z.infer<typeof updateFolderBody>;
